import { createResettableSlice } from 'careand-redux/utils/makeResettable';
import { unwrap, unwrapApi } from 'careand-redux/utils/unwrap';
import PerformanceUtils from 'services/PerformanceUtils';
import { api } from 'careand-redux/api/ApiThunks';
import { v4 as uuidV4 } from 'uuid';
import type { PrescriptionMedication } from '../../generated/api';

export type ActionType = 'prescription' | 'referral' | 'requisition' | 'other_document' | 'other';
export type ActionStatus =
  | 'ready'
  | 'in-progress'
  | 'pending-approval'
  | 'done'
  | 'rejected'
  | 'error';

export type ActionResult = {
  prescription: { medication: PrescriptionMedication | undefined };
  referral: {
    templateId: string;
    templateDescription: string;
    fields: { id: string; value: string }[];
  };
  requisition: {
    templateId: string;
    templateDescription: string;
    fields: { id: string; value: string }[];
  };
  other_document: {
    templateId: string;
    templateDescription: string;
    fields: { id: string; value: string }[];
  };
  other: { note: string };
};

export function matchClinicalAction<T extends ActionType>(
  action: ClinicalAction<any>,
  ...types: T[]
): action is ClinicalAction<T> {
  return types.includes(action.type);
}

export interface ClinicalAction<TType extends ActionType> {
  id: string;
  appointment_id: string;
  description: string;
  status: ActionStatus;
  reason?: string; // Why AI suggested this
  created_at: number;
  updated_at: number;
  error_message?: string;
  type: TType;
  execution_data?: ActionResult[TType];
  executor_message?: string; // Progress message during execution
}

export interface ActionsState {
  // Main store
  byId: Record<string, ClinicalAction<any>>;
}

const initialState: ActionsState = {
  byId: {},
};

const slice = createResettableSlice({
  name: 'Actions',
  initialState,
  reducers: {
    addAction: unwrap((state, action: ClinicalAction<any>) => {
      state.byId[action.id] = action;
    }),
    updateAction: unwrap((state, payload: Partial<ClinicalAction<any>> & { id: string }) => {
      const action = state.byId[payload.id];
      if (!action) {
        return;
      }
      Object.assign(action, payload);
    }),
    removeAction: unwrap((state, action_id: string) => {
      if (!state.byId[action_id]) {
        return;
      }
      delete state.byId[action_id];
    }),
    applyDeltas: unwrap(applyDeltas),
  },
  extraReducers: (builder) => {
    // API endpoints don't exist yet, so we don't add any extra reducers
    // This will be added when backend endpoints are ready
  },
});

function applyDeltas(state: ActionsState, payload: { appointmentId: string; deltas: any[] }) {
  const { appointmentId, deltas } = payload;

  for (const delta of deltas) {
    switch (delta.change) {
      case 'add': {
        const newAction: ClinicalAction<any> = {
          id: uuidV4(),
          appointment_id: appointmentId,
          type: delta.action.type,
          description: delta.action.description,
          status: 'ready',
          reason: delta.reason,
          created_at: Date.now(),
          updated_at: Date.now(),
        };
        state.byId[newAction.id] = newAction;
        break;
      }
      case 'remove': {
        if (!state.byId[delta.action_id]) {
          return;
        }
        delete state.byId[delta.action_id];
        break;
      }
      case 'update': {
        const existing = state.byId[delta.action_id];
        if (!existing) {
          return;
        }
        Object.assign(existing, delta.action);
        existing.updated_at = Date.now();
        break;
      }
    }
  }
}

export default slice.reducer;
export const actionsActions = slice.actions;
